import { PlainButton } from "@jugl-web/ui-kit";
import { cx } from "@jugl-web/utils";
import { ButtonHTMLAttributes, FC, forwardRef, ReactNode } from "react";
import { useEditorToolbar } from "../RichTextarea";
import { ReactComponent as BoldIcon } from "./assets/bold.svg";
import { ReactComponent as BulletListIcon } from "./assets/bullet-list.svg";
import { ReactComponent as ItalicIcon } from "./assets/italic.svg";
import { ReactComponent as OrderedListIcon } from "./assets/ordered-list.svg";
import { ReactComponent as StrikethroughIcon } from "./assets/strikethrough.svg";
import { ReactComponent as UnderlineIcon } from "./assets/underline.svg";

export const TextToolbar = () => {
  const { toolbarState, formatSelection, insertList } = useEditorToolbar();

  const toolbarButtons: ToolbarButtonProps[] = [
    {
      name: "Bold",
      icon: <BoldIcon />,
      isActive: toolbarState.isBold,
      onClick: () => formatSelection("bold"),
    },
    {
      name: "Italic",
      icon: <ItalicIcon />,
      isActive: toolbarState.isItalic,
      onClick: () => formatSelection("italic"),
    },
    {
      name: "Underline",
      icon: <UnderlineIcon />,
      isActive: toolbarState.isUnderline,
      onClick: () => formatSelection("underline"),
    },
    {
      name: "Strikethrough",
      icon: <StrikethroughIcon />,
      isActive: toolbarState.isStrikethrough,
      onClick: () => formatSelection("strikethrough"),
    },
    {
      name: "Bullet List",
      icon: <BulletListIcon />,
      isActive: toolbarState.listType === "bullet",
      onClick: () =>
        insertList(toolbarState.listType === "bullet" ? null : "bullet"),
    },
    {
      name: "Ordered List",
      icon: <OrderedListIcon />,
      isActive: toolbarState.listType === "number",
      onClick: () =>
        insertList(toolbarState.listType === "number" ? null : "number"),
    },
  ];

  return (
    <ToolbarGroup>
      {toolbarButtons.map((button) => (
        <ToolbarButton key={button.name} {...button} />
      ))}
    </ToolbarGroup>
  );
};

interface ToolbarGroupProps {
  children: ReactNode;
}

export const ToolbarGroup: FC<ToolbarGroupProps> = ({ children }) => (
  <div className="flex items-center gap-2">{children}</div>
);

interface ToolbarButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  name: string;
  icon: ReactNode;
  isActive?: boolean;
}

export const ToolbarButton = forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  ({ name, icon, isActive = false, className, ...props }, ref) => (
    <PlainButton
      ref={ref}
      type="button"
      className={cx(
        "flex h-8 w-8 items-center justify-center rounded transition-colors",
        isActive ? "bg-grey-200" : "hover:bg-grey-200",
        className
      )}
      title={name}
      {...props}
    >
      {icon}
    </PlainButton>
  )
);

ToolbarButton.displayName = "ToolbarButton";
